import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpErrorRoot } from '../../models/common/http-error.model';
import { SaleMemberAccessPopupComponent } from '../../sales/sale-member-access-popup/sale-member-access-popup.component';
import { AuthService } from '../../services/auth.service';
import { HttpErrorHandlerService } from '../../services/http-error-handler.service';
import { SalesService } from '../../services/sales.service';
import { VetUserNotificationSettingComponent } from '../../settings/vet-user-notification-setting/vet-user-notification-setting.component';
import { BaseUnsubscribeComponent } from '../../shared-new/components/base-unsubscribe/base-unsubscribe.component';
import { ToastService } from '../../shared-new/services/toast.service';
import { UserProfileComponent } from '../user-profile/user-profile.component';

@Component({
  selector: 'app-vet-profile-notification-detail-popup',
  templateUrl: './vet-profile-notification-detail-popup.component.html',
  styleUrls: ['./vet-profile-notification-detail-popup.component.scss']
})
export class VetProfileNotificationDetailPopupComponent extends BaseUnsubscribeComponent implements OnInit {
  @Input() data: any;
  activeTab: string = 'profile';
  isProfileLoaded = false;         // profile loads automatically
  isNotificationsLoaded = false;
  isSaleAccessLoaded = false;
  /** A sales company viewing a vet: may add contacts, change/delete only the ones its company added (D-S3). */
  salesCompanyView = false;

  @ViewChild(VetUserNotificationSettingComponent, { static: false })
  vetUserNotificationSettingComponent!: VetUserNotificationSettingComponent;

  @ViewChild(SaleMemberAccessPopupComponent, { static: false })
  saleMemberAccessPopupComponent!: SaleMemberAccessPopupComponent;

  constructor(
    public readonly modal: NgbActiveModal,
    private readonly saleService: SalesService,
    private readonly httpErrorHandlerService: HttpErrorHandlerService,
    private readonly toastService: ToastService,
    private readonly authService: AuthService
  ) {
    super();
  }

  ngOnInit(): void {
    this.isProfileLoaded = true;
    const currentUserId = this.authService.getUserTokenResponse()?.userId;
    this.salesCompanyView = this.authService.IsSalesCompany() && +this.data?.userId !== +currentUserId;
  }

  closePopup() {
    // reset all flags so when reopened it’s a fresh state
    this.resetState();
    this.modal.dismiss();
  }

  public loadTab(tab: string): void {
    this.activeTab = tab;
    if (tab === 'notifications' && !this.isNotificationsLoaded) {
      this.isNotificationsLoaded = true;
    }
    if (tab === 'saleaccess' && !this.isSaleAccessLoaded) {
      this.isSaleAccessLoaded = true;
    }
  }

  public get isSaveButtonDisabled(): boolean {
    // Disable if no editable tab has been opened yet
    return !(this.isNotificationsLoaded || this.isSaleAccessLoaded);
  }

  onSave(): void {
    const payload: any = { userId: this.data?.userId };

    if (this.isNotificationsLoaded && this.vetUserNotificationSettingComponent) {
      payload.notifications = this.vetUserNotificationSettingComponent.getFormData();
    }

    if (this.isSaleAccessLoaded && this.saleMemberAccessPopupComponent) {
      payload.saleAccess = this.saleMemberAccessPopupComponent.getFormData();
    }

    if (!payload.notifications && !payload.saleAccess) {
      return; // nothing to save
    }

    this.saleService.updateVetSettings(payload).subscribe({
      next: () => {
        if (payload.notifications && payload.saleAccess) {
          this.toastService.success('Notifications & Member access updated successfully');
        } else if (payload.notifications) {
          this.toastService.success('Notifications updated successfully');
        } else if (payload.saleAccess) {
          this.toastService.success('Member access updated successfully');
        }

        // reset state before closing so next open is fresh
        this.resetState();
        this.modal.close('save');
      },
      error: (error: HttpErrorRoot | any) => {
        if (payload.notifications && (error?.status === 403 || error?.status === 404)) {
          // Contacts half refused (D-S3): nothing was written. Say why and reload what the API holds.
          this.toastService.error(VetUserNotificationSettingComponent.contactErrorMessage(error), 'Not saved');
          this.vetUserNotificationSettingComponent?.initiateForm();
          return;
        }
        this.httpErrorHandlerService.ShowCustomErrorMessage(error);
      }
    });
  }

  private resetState(): void {
    this.activeTab = 'profile';
    this.isProfileLoaded = true;
    this.isNotificationsLoaded = false;
    this.isSaleAccessLoaded = false;
  }
}
